import { API_BASE_URL } from '../config/api';

export interface UserProfile {
  firstName: string | null;
  lastName: string | null;
  studioName: string | null;
  email: string | null;
}

export interface UpdateProfilePayload {
  firstName?: string;
  lastName?: string;
  studioName?: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

const toNonEmptyStringOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value.trim() !== '' ? value : null;

const authHeaders = (token: string): HeadersInit => ({
  Authorization: `Bearer ${token}`,
});

const parseProfile = (parsed: any): UserProfile => {
  const user =
    parsed?.user && typeof parsed.user === 'object' ? parsed.user : parsed;
  return {
    firstName: toNonEmptyStringOrNull(user?.firstName),
    lastName: toNonEmptyStringOrNull(user?.lastName),
    studioName: toNonEmptyStringOrNull(user?.studioName),
    email: toNonEmptyStringOrNull(user?.email),
  };
};

const readErrorMessage = (parsed: any, fallback: string): string => {
  if (typeof parsed?.error === 'string' && parsed.error !== '')
    return parsed.error;
  if (typeof parsed?.message === 'string' && parsed.message !== '')
    return parsed.message;
  return fallback;
};

/**
 * GET /auth/profile. Parses defensively — missing fields come back null
 * and callers fall back to the cached `localStorage.user` values.
 */
export const getProfile = async (token: string): Promise<UserProfile> => {
  const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    method: 'GET',
    headers: authHeaders(token),
  });

  if (!response.ok) {
    throw new Error('Failed to load profile');
  }

  const parsed: any = await response.json();
  return parseProfile(parsed);
};

/**
 * PATCH /auth/profile. Sends only the provided fields (partial updates).
 * Returns the updated profile.
 */
export const updateProfile = async (
  token: string,
  payload: UpdateProfilePayload
): Promise<UserProfile> => {
  const response = await fetch(`${API_BASE_URL}/auth/profile`, {
    method: 'PATCH',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = 'Failed to update profile';
    try {
      const parsed: any = await response.json();
      message = readErrorMessage(parsed, message);
    } catch {
      /* non-JSON error body — keep the fallback message */
    }
    throw new Error(message);
  }

  const parsed: any = await response.json();
  return parseProfile(parsed);
};

/**
 * POST /auth/change-password. The session stays valid — no logout, the
 * stored token keeps working.
 */
export const changePassword = async (
  token: string,
  payload: ChangePasswordPayload
): Promise<void> => {
  const response = await fetch(`${API_BASE_URL}/auth/change-password`, {
    method: 'POST',
    headers: {
      ...authHeaders(token),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    let message = 'Failed to change password';
    try {
      const parsed: any = await response.json();
      message = readErrorMessage(parsed, message);
    } catch {
      /* non-JSON error body — keep the fallback message */
    }
    throw new Error(message);
  }
};
